/**
 * lib/site-entity.ts — the ONE Fridge.com entity (FRIDGE-795).
 *
 * Google's site-name and knowledge-panel logic, Bing, and the LLM retrievers
 * all read the Organization + WebSite nodes to decide WHAT KIND of entity
 * Fridge.com is. Until 09/04/2026 those nodes carried a name, a logo and
 * three social links, and every page type that named the publisher
 * re-declared it without an @id — so nothing said the homepage's
 * Organization, the article's publisher and the press page's author were
 * the same thing. /press even carried its own sameAs with a contested X
 * handle and a LinkedIn page that does not exist.
 *
 * This module is the single source for:
 *   - the two stable @ids every page references (the about page already used
 *     these exact strings; now they are imported, never retyped);
 *   - the ruled tagline (FRIDGE-494 capitalization) the footer renders and
 *     the Organization states as `slogan`;
 *   - the knowsAbout topics — a Wikidata entity where one EXISTS for the
 *     topic itself (every QID below was resolved through the Wikidata API on
 *     09/04/2026 and named by its English label), a plain string otherwise.
 *     A broader entity is never borrowed for a narrower phrase: "refrigerator
 *     water filter" has no entity, so the topic is stated as the entity that
 *     does exist, "Water filter" — true, and exact.
 *
 * Deliberately light (no imports): client components render from it too.
 *
 * The @ids are the PRODUCTION origin by design. An entity id is an identity,
 * not a deployment address — a preview build must still say "I am the same
 * Fridge.com", so these never follow NEXT_PUBLIC_SITE_URL.
 */

export const SITE_ORIGIN = 'https://fridge.com'

/** The Organization node's @id — referenced by every page type. */
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`

/** The WebSite node's @id — what every WebPage's isPartOf points at. */
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`

/** The ruled tagline, exact capitalization (FRIDGE-494). */
export const SITE_TAGLINE = 'Fridges, Freezers, And Kitchen Stuff.'

/** FRIDGE-777: approved comparison title and consistent search/social identity. */
export const SITE_SEARCH_TITLE = 'Fridge.com | Compare Refrigerator & Freezer Prices'
export const SITE_SEARCH_DESCRIPTION = 'Compare prices, price history, parts, filters and rebates at Fridge.com. The authoritative refrigerator and freezer search engine in the United States.'

/**
 * FRIDGE-1518 (owner approved 10/15/2026): the homepage's own search
 * description, shopper-first, for its largest Bing query ("fridge"). Only the
 * homepage uses it: its meta description and its WebPage node. Everywhere else
 * SITE_SEARCH_DESCRIPTION keeps describing Fridge.com: the root layout's
 * default for pages with no description of their own and the share cards, and
 * the Organization and WebSite nodes (FRIDGE-620, FRIDGE-777).
 */
export const HOMEPAGE_SEARCH_DESCRIPTION = 'Compare fridge and freezer prices from major retailers, with price history, matching parts and filters, and local rebates, on Fridge.com.'

/** The only customer-facing contact surface (a form; no email is published). */
export const CONTACT_PATH = '/contact'

/** How Fridge.com works — the methodology page is the publishing principles. */
export const PUBLISHING_PRINCIPLES_PATH = '/about-us/methodology'

export interface KnowsAboutTopic {
  /** The topic's name — the Wikidata English label where an entity exists. */
  name: string
  /** The Wikidata entity page, only where the entity IS this topic. */
  sameAs?: string
}

export const ORGANIZATION_KNOWS_ABOUT: readonly KnowsAboutTopic[] = [
  { name: 'Refrigerator', sameAs: 'https://www.wikidata.org/wiki/Q37828' },
  { name: 'Freezer', sameAs: 'https://www.wikidata.org/wiki/Q2487743' },
  { name: 'Wine refrigerator', sameAs: 'https://www.wikidata.org/wiki/Q2846420' },
  // No Wikidata entity for a beverage center / beverage refrigerator
  // (wbsearchentities empty 09/04/2026) — plain string.
  { name: 'Beverage center' },
  { name: 'Kegerator', sameAs: 'https://www.wikidata.org/wiki/Q6383445' },
  { name: 'Ice maker', sameAs: 'https://www.wikidata.org/wiki/Q4271166' },
  { name: 'Water filter', sameAs: 'https://www.wikidata.org/wiki/Q605696' },
  { name: 'Replacement part', sameAs: 'https://www.wikidata.org/wiki/Q1364774' },
  { name: 'ENERGY STAR', sameAs: 'https://www.wikidata.org/wiki/Q1054823' },
  { name: 'Rebate', sameAs: 'https://www.wikidata.org/wiki/Q48995577' },
  { name: 'Product recall', sameAs: 'https://www.wikidata.org/wiki/Q359495' },
]

/** knowsAbout as schema.org Thing nodes — name always, sameAs only when real. */
export function knowsAboutNodes() {
  return ORGANIZATION_KNOWS_ABOUT.map((t) => ({
    '@type': 'Thing',
    name: t.name,
    ...(t.sameAs ? { sameAs: t.sameAs } : {}),
  }))
}

/**
 * The customer-support contact point. `siteUrl` is the rendering origin
 * (the layout passes its SITE_URL) so the link resolves on any deployment;
 * the @ids above stay production regardless.
 */
export function contactPointNode(siteUrl: string = SITE_ORIGIN) {
  return {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: `${siteUrl}${CONTACT_PATH}`,
    areaServed: 'US',
    availableLanguage: 'English',
  }
}

/**
 * The minimal Organization REFERENCE a page-type node uses as publisher or
 * author — @id plus the name and url a validator wants on a reference node.
 * The full entity (logo, description, sameAs, knowsAbout, …) is stated ONCE,
 * by the layout's buildOrganizationSchema(); JSON-LD merges on @id.
 */
export function organizationRef(siteUrl: string = SITE_ORIGIN) {
  return { '@type': 'Organization', '@id': ORGANIZATION_ID, name: 'Fridge.com', url: siteUrl }
}

/** The minimal WebSite reference for a WebPage's isPartOf. */
export function webSiteRef(siteUrl: string = SITE_ORIGIN) {
  return { '@type': 'WebSite', '@id': WEBSITE_ID, name: 'Fridge.com', url: siteUrl }
}
